/**
 * Support feedback client — the app side of the OM Asiakasdeski.
 *
 * Collects what a support person needs to reproduce a problem (browser, view,
 * recent errors) and nothing else. Reflection content, transcripts and answers
 * are never collected: for a participant the desk only ever learns which view
 * they were on. What gets attached is shown to the user before sending
 * (transparency requirement of the intake contract).
 */

import axios from 'axios';

import { sanitizeChatActions, type ChatAction, type HostPage } from './hostGuide';
import type { SessionFacts } from './supportChatNews';

const getApiBaseUrl = (): string => {
    // Explicit override first, so a dev instance can talk to a backend on another
    // port without editing code (same pattern as voiceApi).
    const configured = import.meta.env.VITE_API_URL;
    if (typeof configured === 'string' && configured.length > 0) {
        return configured.replace(/\/$/, '');
    }

    const protocol = window.location.protocol;
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//${hostname}:4000`;
    }
    return '';
};

const api = axios.create({
    baseURL: getApiBaseUrl(),
    withCredentials: true,
});

const getAuthToken = (): string | null => {
    try {
        const stored = localStorage.getItem('art-of-thinking::auth');
        if (stored) {
            const parsed = JSON.parse(stored);
            return parsed.token || null;
        }
    } catch {
        // Parsing failed — send as an anonymous visitor
    }
    return null;
};

api.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ---------------------------------------------------------------------------
// Error ring buffer
// ---------------------------------------------------------------------------

const MAX_ERRORS = 5;
const recentErrors: string[] = [];

const pushError = (label: string): void => {
    const line = `${new Date().toISOString().slice(11, 19)} ${label}`.slice(0, 300);
    recentErrors.push(line);
    while (recentErrors.length > MAX_ERRORS) {
        recentErrors.shift();
    }
};

/** Start remembering the last few JS errors. Call once at startup. */
export const initSupportErrorBuffer = (): void => {
    window.addEventListener('error', (event) => {
        pushError(`${event.message} @ ${String(event.filename || '').split('/').pop()}:${event.lineno}`);
    });
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        pushError(`unhandled: ${reason instanceof Error ? reason.message : String(reason)}`);
    });
};

// ---------------------------------------------------------------------------
// What we attach
// ---------------------------------------------------------------------------

export type SupportAttachments = {
    device: string;
    context: string;
};

export const collectAttachments = (): SupportAttachments => {
    const device = [
        navigator.userAgent,
        `screen ${window.screen.width}x${window.screen.height}`,
        `window ${window.innerWidth}x${window.innerHeight}`,
        `lang ${navigator.language}`,
    ].join(' | ').slice(0, 1000);

    // Path plus the page's own title, so the desk reads a situation ("Science",
    // "Build your shared language") and not just a route. Path only: it names the
    // view (and the share code already in the URL), never anything the participant
    // said. Query strings are dropped because they can carry invite tokens.
    const pageTitle = document.title.replace(/\s*—\s*Art of Thinking$/, '').trim();
    const contextParts = [
        pageTitle && pageTitle !== 'Art of Thinking'
            ? `view ${window.location.pathname} (${pageTitle})`
            : `view ${window.location.pathname}`,
    ];
    if (recentErrors.length > 0) {
        contextParts.push(`errors: ${recentErrors.join(' ;; ')}`);
    }

    return { device, context: contextParts.join('\n').slice(0, 4000) };
};

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

/**
 * Where the message was written. The backend decides the sender's role from its
 * own auth, but the surface tells it whether an anonymous sender is a visitor
 * asking to be contacted or a participant inside an exercise — two different
 * privacy defaults.
 */
export type SupportSurface = 'public' | 'participant';

export type SupportFeedbackInput = {
    message: string;
    subject?: string;
    images?: string[];
    device: string;
    context: string;
    /** Only used for anonymous senders; typing it IS the consent to store it. */
    email?: string;
    surface: SupportSurface;
};

export const sendSupportFeedback = async (input: SupportFeedbackInput): Promise<void> => {
    await api.post('/api/support/feedback', input);
};

// ---------------------------------------------------------------------------
// Host support chat (#1046)
// ---------------------------------------------------------------------------

export type SupportChatReply = {
    /** tuki-chat conversation id — pass back to continue the same thread */
    sessionId: string;
    reply: string;
    /** true = the conversation was handed to the team, follow-up comes by email */
    escalated: boolean;
    /** Stage 4 (#1165): proposals only, rendered as deep links into the real UI. */
    actions: ChatAction[];
    /** The guide said it is unsure or has no answer (#1077): offer the handover. */
    lowConfidence: boolean;
    /** The live-facts snapshot could not be built; the host is told (#1077 c). */
    contextLimited: boolean;
};

/**
 * Whether the backend has the chat service configured. False also on any
 * error: the widget then shows the graceful "use feedback instead" state
 * rather than a chat panel that cannot answer.
 */
export const fetchSupportChatAvailability = async (): Promise<boolean> => {
    try {
        const { data } = await api.get('/api/support/chat/availability');
        return (data as { available?: boolean })?.available === true;
    } catch {
        return false;
    }
};

/**
 * Whitelisted session facts for the arrival news diff (#1064). Null on any
 * error — no news is always a safe answer.
 */
export const fetchSupportChatContext = async (): Promise<SessionFacts[] | null> => {
    try {
        const { data } = await api.get('/api/support/chat/context');
        const sessions = (data as { sessions?: unknown })?.sessions;
        return Array.isArray(sessions) ? (sessions as SessionFacts[]) : null;
    } catch {
        return null;
    }
};

export const sendSupportChatMessage = async (input: {
    message: string;
    sessionId?: string;
    /** Which host page is open (#1165); the backend whitelists it into the snapshot. */
    currentPage?: HostPage | null;
}): Promise<SupportChatReply> => {
    const { data } = await api.post('/api/support/chat', {
        message: input.message,
        sessionId: input.sessionId,
        ...(input.currentPage ? { currentPage: input.currentPage } : {}),
    });
    const body = data as {
        sessionId?: string;
        reply?: string;
        escalated?: boolean;
        actions?: unknown;
        lowConfidence?: boolean;
        contextLimited?: boolean;
    };
    return {
        sessionId: typeof body?.sessionId === 'string' ? body.sessionId : '',
        reply: typeof body?.reply === 'string' ? body.reply : '',
        escalated: body?.escalated === true,
        actions: sanitizeChatActions(body?.actions),
        lowConfidence: body?.lowConfidence === true,
        contextLimited: body?.contextLimited === true,
    };
};

export type SupportEscalationReason = 'low_confidence' | 'no_answer' | 'chat_failed' | 'host_request';

/**
 * "Send this conversation to the team" (#1165, #1077): the transcript goes to the
 * desk as one intake, nothing else changes.
 */
export const escalateSupportChat = async (input: {
    messages: { from: 'host' | 'support'; text: string }[];
    reason: SupportEscalationReason;
    sessionId?: string;
    currentPage?: HostPage | null;
}): Promise<void> => {
    await api.post('/api/support/chat/escalate', {
        messages: input.messages.map((m) => ({ from: m.from, text: m.text.slice(0, 2000) })),
        reason: input.reason,
        sessionId: input.sessionId,
        ...(input.currentPage ? { currentPage: input.currentPage } : {}),
    });
};
